import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { startDiscordConnect } from "@/lib/discordConnect";
import { useTranslation } from "react-i18next";
import { Trophy, Flame, Lock, Sparkles, Award, Scroll, MessageSquare } from "lucide-react";
import api, { API_BASE } from "@/lib/api";
import Certificates from "@/pages/Certificates";

/**
 * Full 26-badge Achievements page.
 * Grouped by category (Streak / Learning / Desk / Community / Prestige).
 * Locked badges are dimmed but visible so members see what's ahead.
 */
const CATEGORY_ORDER = ["streak", "learning", "desk", "community", "prestige"];
const DISCORD_BADGE = "icebreaker";

const TIER_STYLES = {
  bronze:   "border-orange-200 bg-gradient-to-br from-orange-50/50 to-white",
  amber:    "border-amber-300 bg-gradient-to-br from-amber-50 to-white",
  gold:     "border-yellow-400 bg-gradient-to-br from-yellow-50 to-white",
  platinum: "border-slate-300 bg-gradient-to-br from-slate-50 to-white",
  prestige: "border-amber-400 bg-gradient-to-br from-white via-white to-amber-50 ring-2 ring-amber-200",
};

export default function Achievements() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState([]);
  const [earned, setEarned] = useState([]);
  const [streak, setStreak] = useState(null);
  const [tab, setTab] = useState("badges"); // "badges" | "certifications"

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [c, e, s] = await Promise.all([
          api.get("/achievements/catalog"),
          api.get("/achievements/me"),
          api.get("/achievements/streak"),
        ]);
        if (!alive) return;
        setCatalog(c.data);
        setEarned(e.data);
        setStreak(s.data);
      } catch (_e) { /* ignore */ }
    })();
    return () => { alive = false; };
  }, []);

  const earnedMap = new Map(earned.map((e) => [e.kind, e.earned_at]));
  const totalEarned = earned.length;
  const totalCatalog = catalog.length;

  const byCategory = CATEGORY_ORDER.reduce((acc, cat) => {
    acc[cat] = catalog.filter((c) => c.category === cat);
    return acc;
  }, {});

  return (
    <div data-testid="achievements-page" className="space-y-10 max-w-6xl">
      {/* Header */}
      <div>
        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-amber-700 font-semibold mb-3">
          <Trophy size={14} /> {t("achievementsPage.eyebrow")}
        </div>
        <h1 className="font-display font-bold text-4xl text-slate-900 tracking-tight">
          {t("achievementsPage.title")}
        </h1>
        <p className="mt-3 text-slate-600 max-w-2xl">
          {t("achievementsPage.subtitle")}
        </p>

        {streak && totalCatalog > 0 && (
          <div className="mt-6 flex flex-wrap gap-3">
            <div className="inline-flex items-center gap-2 rounded-full bg-orange-50 border border-orange-200 px-4 py-2">
              <Flame size={16} className="text-orange-500" />
              <span data-testid="achievements-streak" className="text-sm font-bold text-orange-800">
                {t("achievementsPage.streak", { count: streak.login_streak })}
              </span>
              <span className="text-xs text-orange-600">
                {t("achievementsPage.longest", { n: streak.longest_streak })}
              </span>
            </div>
            <div className="inline-flex items-center gap-2 rounded-full bg-amber-50 border border-amber-200 px-4 py-2">
              <Trophy size={16} className="text-amber-600" />
              <span className="text-sm font-bold text-amber-800">
                {t("achievementsPage.unlocked", { earned: totalEarned, total: totalCatalog })}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Tab strip: Badges | Certifications */}
      <div className="border-b border-slate-200 -mt-2">
        <div className="inline-flex gap-1">
          <button
            type="button"
            data-testid="achievements-tab-badges"
            onClick={() => setTab("badges")}
            className={`px-5 py-3 -mb-px border-b-2 text-sm font-bold tracking-tight inline-flex items-center gap-2 transition-colors ${
              tab === "badges"
                ? "border-amber-500 text-slate-900"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            <Trophy size={15} />
            {t("achievementsPage.tabs.badges")}
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
              tab === "badges" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-500"
            }`}>{totalEarned}/{totalCatalog}</span>
          </button>
          <button
            type="button"
            data-testid="achievements-tab-certifications"
            onClick={() => setTab("certifications")}
            className={`px-5 py-3 -mb-px border-b-2 text-sm font-bold tracking-tight inline-flex items-center gap-2 transition-colors ${
              tab === "certifications"
                ? "border-amber-500 text-slate-900"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            <Scroll size={15} />
            {t("achievementsPage.tabs.certifications")}
          </button>
        </div>
      </div>

      {/* Certifications tab: reuse the existing Certificates page */}
      {tab === "certifications" && (
        <div data-testid="achievements-certifications-tab" className="-mt-4">
          <Certificates />
        </div>
      )}

      {/* Badges tab (default): render the achievement grid */}
      {tab === "badges" && (
        <>
      {/* Grid by category */}
      {CATEGORY_ORDER.map((cat) => {
        const items = byCategory[cat] || [];
        if (!items.length) return null;
        return (
          <section key={cat} data-testid={`achievements-section-${cat}`}>
            <div className="flex items-baseline justify-between mb-4">
              <div>
                <h2 className="font-display font-bold text-xl text-slate-900 tracking-tight">
                  {t(`achievementsPage.categories.${cat}.label`)}
                </h2>
                <p className="text-xs text-slate-500 uppercase tracking-widest font-semibold mt-1">
                  {t(`achievementsPage.categories.${cat}.tag`)}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {items.map((b) => {
                const isEarned = earnedMap.has(b.kind);
                const earnedAt = earnedMap.get(b.kind);
                return (
                  <div
                    key={b.kind}
                    data-testid={`achievement-${b.kind}`}
                    className={`relative rounded-2xl border p-3 sm:p-4 text-center transition-shadow ${
                      isEarned
                        ? `${TIER_STYLES[b.tier]} shadow-sm hover:shadow-md`
                        : b.kind === DISCORD_BADGE && !user?.discord_linked
                          ? "border-[#5865F2]/40 bg-white"
                          : "border-slate-200 bg-white opacity-70"
                    }`}
                  >
                    {!isEarned && (
                      <div className="absolute top-2 left-2 w-6 h-6 rounded-full bg-white shadow-sm flex items-center justify-center border border-slate-200">
                        <Lock size={11} className="text-slate-500" />
                      </div>
                    )}
                    {isEarned && b.tier === "prestige" && (
                      <div className="absolute top-2 right-2 inline-flex items-center gap-0.5 text-[8px] font-bold uppercase tracking-widest text-amber-800 bg-amber-100 border border-amber-300 rounded-full px-1.5 py-0.5">
                        <Sparkles size={8} /> {t("achievementsPage.rare")}
                      </div>
                    )}
                    <img
                      src={`${API_BASE}/badges/${b.kind}.png?v=2`}
                      alt={b.title}
                      className={`w-full aspect-square object-contain mx-auto mb-2 ${
                        isEarned ? "" : "grayscale opacity-40"
                      }`}
                    />
                    <div className="text-sm font-bold text-slate-900 leading-tight">
                      {b.title}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1 leading-tight min-h-[28px]">
                      {b.description}
                    </div>
                    <div className={`text-[9px] font-bold uppercase tracking-widest mt-2 ${
                      isEarned ? "text-amber-700" : "text-slate-400"
                    }`}>
                      {isEarned
                        ? t("achievementsPage.earnedOn", { date: new Date(earnedAt).toLocaleDateString(undefined, { month: "short", day: "numeric" }) })
                        : b.tier
                      }
                    </div>
                    {!isEarned && b.kind === DISCORD_BADGE && !user?.discord_linked && (
                      <button
                        type="button"
                        onClick={() => startDiscordConnect(navigate)}
                        data-testid="achievement-icebreaker-connect"
                        className="mt-3 w-full inline-flex items-center justify-center gap-1.5 rounded-lg bg-[#5865F2] hover:bg-[#4752c4] text-white text-[11px] leading-tight font-semibold px-2.5 py-2 transition-colors"
                      >
                        <MessageSquare size={11} className="shrink-0" /> {t("achievementsPage.connectToUnlock", { defaultValue: "Connect Discord to unlock" })}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}
        </>
      )}
    </div>
  );
}
